import React from 'react';
import { motion } from 'framer-motion';
import { Search, Sliders, HeartHandshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const steps = [{
  icon: Search,
  number: '01',
  title: 'Antes de tocarte, te evalúa.',
  description: "Analiza tu tipo de piel, tu historia, tus hábitos, tus preocupaciones específicas. Construye un mapa de lo que tu piel necesita — no de lo que funciona 'en general.'"
}, {
  icon: Sliders,
  number: '02',
  title: 'Luego diseña tu protocolo.',
  description: 'Combinando técnicas avanzadas de cuidado coreano con tecnología de última generación disponible en su spa, Kate selecciona el tratamiento exacto para tu condición. No más. No menos.'
}, {
  icon: HeartHandshake,
  number: '03',
  title: 'Y te acompaña en el proceso.',
  description: 'No te vende una sesión y desaparece. Te guía. Te educa. Te enseña a entender tu propia piel para que los resultados duren mucho más allá de la sala de tratamiento.'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 18
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};
export function ProtocolSection() {
  const {
    t
  } = useLanguage();
  const steps = [{
    icon: HeartHandshake,
    number: '03',
    title: t('protocol.s3_title'),
    description: t('protocol.s3_desc')
  }];
  return <section id="protocolo" className="py-12 md:py-24 bg-brand-cream relative" data-id="element-366" data-mp="x5bo08zo0ljf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367" data-mp="t49lnjagbtd8">
        <div className="relative text-center max-w-4xl mx-auto mb-12 md:mb-20" data-id="element-368" data-mp="t3zxm18yvwa6">
          {/* Soft gold halo behind the heading */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-[420px] w-[720px] max-w-[130%] rounded-full opacity-70" style={{
          background: 'radial-gradient(closest-side, rgba(184,134,11,0.13), rgba(184,134,11,0.04) 55%, transparent 72%)'
        }} data-id="element-369" data-mp="124y1mjjeo3sr" />

          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.25
        }} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.11,
              delayChildren: 0.05
            }
          }
        }} data-id="element-370" data-mp="17tfyuyspt6c1">
            <motion.p variants={fadeUp} className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-brand-gold font-medium mb-5" data-id="element-371" data-mp="1luikjafssemf">
              {t('protocol.eyebrow')}
            </motion.p>

            <motion.h2 variants={fadeUp} className="text-[28px] sm:text-4xl md:text-5xl font-serif text-brand-brown leading-[1.15] max-w-3xl mx-auto" data-id="element-372" data-mp="1vpty3pfgzfsl">
              {t('protocol.title')}
            </motion.h2>

            {/* Fine separator with a gold diamond */}
            <motion.div variants={fadeUp} aria-hidden="true" className="flex items-center justify-center gap-3 my-7 md:my-9" data-id="element-373" data-mp="29j1qvkfaehbv">
              <span className="block h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-brand-gold/60" data-id="element-374" data-mp="2eieggcxjm2i5" />
              <span className="block h-1.5 w-1.5 rotate-45 bg-brand-gold" data-id="element-375" data-mp="2on4isjwuz8mw" />
              <span className="block h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-brand-gold/60" data-id="element-376" data-mp="2jlf6l0xwgox8" />
            </motion.div>

            <motion.p variants={fadeUp} className="text-base md:text-lg text-brand-brown-soft leading-relaxed max-w-[34ch] mx-auto" data-id="element-377" data-mp="2enng8bcwlav5">
              {t('protocol.p1')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-5 text-2xl sm:text-3xl md:text-[2.5rem] font-serif italic text-brand-brown leading-snug" data-id="element-378" data-mp="37bj7h0jpeakj">
              {t('protocol.p2')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-6 text-base md:text-lg text-brand-charcoal/85 leading-[1.9] max-w-[56ch] mx-auto" data-id="element-379" data-mp="2xkew0at8l7ug">
              {t('protocol.p3')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-12 md:mt-16 text-[11px] md:text-xs uppercase tracking-[0.18em] text-brand-brown-soft" data-id="element-380" data-mp="3q43mbe2kif5c">
              {t('protocol.approach_intro')}
            </motion.p>

            {/* Four-step method — horizontal rows framed by gold diamonds */}
            <div className="relative mt-8 md:mt-10 max-w-3xl mx-auto space-y-3.5" data-id="element-381" data-mp="3atou2ff15wsj">
              {['a1', 'a2', 'a3', 'a4'].map((key, i) => <motion.div key={key} variants={fadeUp} className="group flex items-start gap-3 sm:gap-4" data-id="element-382" data-mp="2wv7ckzfpxgj2" data-mpi={i}>
                  <span aria-hidden="true" className="mt-[19px] h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-brand-gold/70 transition-all duration-500 group-hover:bg-brand-gold group-hover:scale-125" data-id="element-383" data-mp="30wj5bdtw2hvh" />
                  <span aria-hidden="true" className="mt-[22px] block h-px w-4 sm:w-6 flex-shrink-0 bg-brand-gold/50 transition-all duration-500 group-hover:w-8 sm:group-hover:w-10 group-hover:bg-brand-gold/80" data-id="element-384" data-mp="3tlhrqn51o66l" />

                  <div className="relative flex flex-1 items-start gap-3 sm:gap-4 rounded-sm border border-brand-gold/15 bg-white px-4 py-3 shadow-[0_1px_3px_rgba(90,60,40,0.05)] transition-all duration-500 group-hover:border-brand-gold/35 group-hover:shadow-[0_4px_14px_rgba(90,60,40,0.08)]" data-id="element-385" data-mp="bds8gjxomgez">
                    <span className="w-7 flex-shrink-0 font-serif text-[13px] tracking-[0.2em] text-brand-gold leading-[1.6]" data-id="element-386" data-mp="20sji3ddumwwx">
                      {`0${i + 1}`}
                    </span>
                    <p className="text-left text-[13.5px] md:text-sm leading-[1.6] text-brand-charcoal/90" data-id="element-387" data-mp="2amjrhrg5yobg">
                      {t(`protocol.${key}`)}
                    </p>
                    {/* Fine gold rule filling the remaining space */}
                    <span aria-hidden="true" className="hidden sm:block mt-[10px] flex-1 h-px bg-gradient-to-r from-brand-gold/45 to-brand-gold/10 transition-all duration-500 group-hover:from-brand-gold group-hover:to-brand-gold/30" data-id="element-388" data-mp="1cxqdecdes6e8" />
                  </div>

                  <span aria-hidden="true" className="hidden sm:block mt-[10px] h-px w-6 flex-shrink-0 bg-brand-gold/50 transition-all duration-500 group-hover:w-10 group-hover:bg-brand-gold/80" data-id="element-389" data-mp="1mxh92hdpgrfw" />
                  <span aria-hidden="true" className="hidden sm:block mt-[7px] h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-brand-gold/70 transition-all duration-500 group-hover:bg-brand-gold group-hover:scale-125" data-id="element-390" data-mp="2ycme3s1b722o" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Premium framed portrait */}
        <motion.div className="group relative max-w-3xl mx-auto mb-12 md:mb-20" initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-391" data-mp="3kb0kpdvcf95q">
          <div className="absolute inset-0 bg-brand-gold/10 translate-x-4 translate-y-4 rounded-sm transition-transform duration-700 group-hover:translate-x-6 group-hover:translate-y-6" data-id="element-392" data-mp="31bwf6f30fxit"></div>
          <div className="absolute inset-0 border border-brand-gold/40 -translate-x-4 -translate-y-4 rounded-sm transition-transform duration-700 group-hover:-translate-x-6 group-hover:-translate-y-6" data-id="element-393" data-mp="23uo5jxi4jn94"></div>

          <div className="relative z-10 overflow-hidden rounded-sm border border-brand-gold/30 shadow-xl bg-brand-dark" data-id="element-394" data-mp="lyvvv2n84jc8">
            <img src="https://cdn.magicpatterns.com/uploads/nnCMRw8mvAvPahteyMdWUW/EL_PROTOCOLO_PIEL_SANA,NO_PERFECTA.png" alt="Kate en su cabina de tratamiento en Tairu Aesthetics" loading="lazy" className="w-full max-h-[560px] md:max-h-none object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-105" data-id="element-395" data-mp="gz9hgpvnsx1u" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" data-id="element-396" data-mp="29livwev4cht"></div>
            <div className="absolute bottom-5 left-5 right-5 z-20" data-id="element-397" data-mp="3pfkwy941f0ui">
              <span className="block h-px w-10 bg-brand-gold mb-3 transition-all duration-700 group-hover:w-20" data-id="element-398" data-mp="3phfgbiznukwh"></span>
              <p className="text-glow-white-strong text-white font-serif text-lg md:text-2xl leading-snug" data-id="element-399" data-mp="3kic9pu4186me">
                {t('protocol.image_caption')}
              </p>
            </div>
            <motion.div className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" initial={{
            x: '-50%',
            opacity: 0
          }} whileInView={{
            x: '450%',
            opacity: 1
          }} viewport={{
            once: true,
            amount: 0.25
          }} transition={{
            duration: 1.7,
            delay: 0.6,
            ease: 'easeInOut'
          }} data-id="element-400" data-mp="1843p2zbakruo" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-401" data-mp="fhj2ec5rh3xg">
          {steps.map((step, index) => {})}
        </div>
      </div>
    </section>;
}